// src/config/tourConfig.js
import { RiAddCircleLine } from "react-icons/ri";
import {
  MdDashboard,
  MdSearch,
  MdViewModule,
  MdOutlineExitToApp,
  MdOutlineVpnKeyOff,
} from "react-icons/md";
import {
  LuFileKey2,
  LuUserCircle2,
  LuPhoneCall,
  LuCombine,
  LuSparkles,
  LuAtom,
  LuGlobe,
  LuWebhook,
} from "react-icons/lu";
import CreateAppTour from "../Component/Tour/Described/CreateAppTour";
import SearchTour from "../Component/Tour/Described/SearchTour";
import CardViewTour from "../Component/Tour/Described/CardViewTour";
import AppTour from "../Component/Tour/Described/AppTour";
import CredentialsTour from "../Component/Tour/Described/CredentialsTour";
import {
  CredentialsAppName,
  CredentialsAuthKey,
  CredentialsRestKey,
} from "../Component/Tour/Described/CredentialsTour";
import AIAgentTour, {
  EnableLiveAgentToggle,
  EnableGuestLiveChat,
  EnableVideoSettings,
} from "../Component/Tour/Described/AIAgentTour";

export const tourConfig = {
  // Global toggle - set to false to disable all tours temporarily
  globallyEnabled: true,

  // Default delay before tour starts (in ms)
  defaultDelay: 1500,

  // Route-based tours
  routes: {
    "/": {
      enabled: true,
      storageKey: "tour_allApps_completed",
      delay: 1500,
      steps: [
        {
          targetSelector: '[data-tour="create-app"]',
          title: "Create Your First App",
          description: <CreateAppTour />,
          icon: <RiAddCircleLine size={24} />,
          placement: "bottom",
        },
        {
          targetSelector: '[data-tour="search-apps"]',
          title: "Search Applications",
          description: <SearchTour />,
          icon: <MdSearch size={24} />,
          placement: "bottom",
        },
        {
          targetSelector: '[data-tour="view-toggle"]',
          title: "Switch View Mode",
          description: <CardViewTour />,
          icon: <MdViewModule size={24} />,
          placement: "bottom",
        },
        {
          targetSelector: "[data-tour=click-on-app]",
          title: "Click on the app",
          description: <AppTour />,
          icon: <MdOutlineExitToApp size={24} />,
          placement: "bottom",
          // ✅ This step just points to the app card, user clicks it
        },
      ],
    },

    "/app_detail/:region/:app_name": {
      enabled: true,
      storageKey: "tour_appDetail_completed",
      delay: 3000, // ✅ Increased delay to wait for page to fully load
      steps: [
        {
          targetSelector: '[data-tour="credentials"]',
          title: "Credentials",
          description: <CredentialsTour />,
          icon: <LuFileKey2 size={24} />,
          placement: "right",
          // ✅ Auto-click to expand credentials and navigate
          onStepEnter: () => {
            setTimeout(() => {
              const credElement = document.querySelector(
                '[data-tour="credentials"]',
              );
              if (credElement) {
                console.log("🖱️ Auto-clicking credentials");
                credElement.click();
              }
            }, 500); // Small delay to ensure element is interactive
          },
        },
        {
          targetSelector: '[data-tour="copy-app-name"]',
          title: "Copy App Name",
          description: <CredentialsAppName />,
          icon: <MdOutlineExitToApp size={24} />,
          placement: "right",
        },
        {
          targetSelector: '[data-tour="copy-auth-key"]',
          title: "Copy Auth Key",
          description: <CredentialsAuthKey />,
          icon: <MdOutlineVpnKeyOff size={24} />,
          placement: "right",
        },
        {
          targetSelector: '[data-tour="copy-rest-api-key"]',
          title: "Copy Rest Api Key",
          description: <CredentialsRestKey />,
          icon: <MdOutlineVpnKeyOff size={24} />,
          placement: "right",
        },
        {
          targetSelector: '[data-tour="live-agent"]',
          title: "Live Agent",
          description: <AIAgentTour />,
          icon: <LuPhoneCall size={24} />,
          placement: "right",
          onStepEnter: () => {
            setTimeout(() => {
              const liveAgentLink = document.querySelector(
                '[data-tour="live-agent"]',
              );
              if (liveAgentLink) {
                console.log("🖱️ Auto-clicking live agent");
                liveAgentLink.click();
              }
            }, 500);
          },
        },
        // {
        //   targetSelector: '[data-tour="enable-live-agent"]',
        //   title: "Enable Live Agent Features",
        //   description: <EnableLiveAgentToggle />,
        //   icon: <LuPhoneCall size={24} />,
        //   placement: "top",
        // },

        // ✅ Guest Live Chat tour step.
        //    Points to the toggle we added in LiveBot.js
        //    (data-tour="enable-guest-live-chat").
        //    Assumes the user has already landed on the Live Agent tab
        //    via the previous step.
        {
          targetSelector: '[data-tour="enable-guest-live-chat"]',
          title: "Allow Guest Live Chat",
          description: <EnableGuestLiveChat />,
          icon: <LuPhoneCall size={24} />,
          placement: "top",
          // Scroll the toggle into view before highlighting, since
          // Live User Settings may be below the fold.
          onStepEnter: () => {
            setTimeout(() => {
              const guestToggle = document.querySelector(
                '[data-tour="enable-guest-live-chat"]',
              );
              if (guestToggle) {
                guestToggle.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                console.log("🖱️ Scrolled to guest live chat toggle");
              }
            }, 500);
          },
        },

        // ✅ Video Call Media tour step.
        //    Points to the section wrapper we added in LiveBot.js
        //    (data-tour="enable-video-settings").
        {
          targetSelector: '[data-tour="enable-video-settings"]',
          title: "Video Call Media",
          description: <EnableVideoSettings />,
          icon: <LuPhoneCall size={24} />,
          placement: "top",
          onStepEnter: () => {
            setTimeout(() => {
              const videoSection = document.querySelector(
                '[data-tour="enable-video-settings"]',
              );
              if (videoSection) {
                videoSection.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                console.log("🖱️ Scrolled to video settings section");
              }
            }, 500);
          },
        },
      ],
    },
  },

  // Helper: Check if tour should show for current path
  shouldShowTour(pathname) {
    const route = this.findMatchingRoute(pathname);
    if (!route) return false;

    const config = this.routes[route];
    return config.enabled && !localStorage.getItem(config.storageKey);
  },

  // Helper: Get tour config for current path
  getTourForRoute(pathname) {
    const route = this.findMatchingRoute(pathname);
    return route ? this.routes[route] : null;
  },

  // Helper: Find matching route pattern
  findMatchingRoute(pathname) {
    return Object.keys(this.routes).find((route) => {
      const pattern = route.replace(/:[^\s/]+/g, "[^/]+").replace(/\//g, "\\/");
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(pathname);
    });
  },

  // Helper: Mark tour as seen
  markTourSeen(pathname) {
    const tour = this.getTourForRoute(pathname);
    if (tour) {
      localStorage.setItem(tour.storageKey, "true");
    }
  },

  // Helper: Reset all tours (useful for development)
  resetAllTours() {
    Object.values(this.routes).forEach((config) => {
      localStorage.removeItem(config.storageKey);
    });
  },
};